import { Injectable, computed, signal, inject } from '@angular/core';
import { BreakpointObserver } from '@angular/cdk/layout';
import { toSignal } from '@angular/core/rxjs-interop';
import { map } from 'rxjs';

export type MobileView = 'list' | 'map';
export type LayoutMode = 'split' | 'map';
export type ListViewMode = 'list' | 'grid';

export interface LightboxState {
  open: boolean;
  images: string[];
  currentIndex: number;
}

@Injectable({ providedIn: 'root' })
export class UiService {
  private static readonly LIST_VIEW_MODE_KEY = 'zaklepuj_list_view_mode';
  private static readonly MOBILE_BREAKPOINT = '(max-width: 768px)';

  private readonly breakpointObserver = inject(BreakpointObserver);

  private readonly _mobileView = signal<MobileView>('list');
  private readonly _layoutMode = signal<LayoutMode>('split');
  private readonly _hoveredPropertyId = signal<number | null>(null);
  private readonly _selectedPropertyId = signal<number | null>(null);
  private readonly _listViewMode = signal<ListViewMode>(this.loadListViewMode());

  readonly isMobile = toSignal(
    this.breakpointObserver.observe(UiService.MOBILE_BREAKPOINT).pipe(
      map(result => result.matches)
    ),
    { initialValue: false }
  );
  private readonly _lightbox = signal<LightboxState>({
    open: false,
    images: [],
    currentIndex: 0,
  });

  readonly mobileView = this._mobileView.asReadonly();
  readonly layoutMode = this._layoutMode.asReadonly();
  readonly listViewMode = this._listViewMode.asReadonly();
  readonly effectiveListViewMode = computed<ListViewMode>(() =>
    this.isMobile() ? 'list' : this._listViewMode()
  );
  readonly hoveredPropertyId = this._hoveredPropertyId.asReadonly();
  readonly selectedPropertyId = this._selectedPropertyId.asReadonly();
  readonly isDetailView = computed(() => this._selectedPropertyId() !== null);
  readonly lightbox = this._lightbox.asReadonly();

  setMobileView(view: MobileView): void {
    this._mobileView.set(view);
  }

  toggleMobileView(): void {
    this._mobileView.update((v) => (v === 'list' ? 'map' : 'list'));
  }

  setListViewMode(mode: ListViewMode): void {
    this._listViewMode.set(mode);
    this.saveListViewMode(mode);
  }

  toggleListViewMode(): void {
    this._listViewMode.update((v) => {
      const next = v === 'list' ? 'grid' : 'list';
      this.saveListViewMode(next);
      return next;
    });
  }

  setLayoutMode(mode: LayoutMode): void {
    this._layoutMode.set(mode);
  }

  setHoveredProperty(id: number | null): void {
    this._hoveredPropertyId.set(id);
  }

  selectProperty(id: number): void {
    this._selectedPropertyId.set(id);
  }

  clearSelectedProperty(): void {
    this._selectedPropertyId.set(null);
  }

  openLightbox(images: string[], startIndex = 0): void {
    this._lightbox.set({
      open: true,
      images,
      currentIndex: startIndex,
    });
  }

  closeLightbox(): void {
    this._lightbox.update((state) => ({
      ...state,
      open: false,
    }));
  }

  nextImage(): void {
    this._lightbox.update((state) => ({
      ...state,
      currentIndex: (state.currentIndex + 1) % state.images.length,
    }));
  }

  prevImage(): void {
    this._lightbox.update((state) => ({
      ...state,
      currentIndex: state.currentIndex === 0 ? state.images.length - 1 : state.currentIndex - 1,
    }));
  }

  goToImage(index: number): void {
    this._lightbox.update((state) => ({
      ...state,
      currentIndex: Math.max(0, Math.min(index, state.images.length - 1)),
    }));
  }

  private loadListViewMode(): ListViewMode {
    const saved = localStorage.getItem(UiService.LIST_VIEW_MODE_KEY);
    return saved === 'list' || saved === 'grid' ? saved : 'grid';
  }

  private saveListViewMode(mode: ListViewMode): void {
    localStorage.setItem(UiService.LIST_VIEW_MODE_KEY, mode);
  }
}
